import React, { useMemo, useRef, useState, useEffect } from 'react';
import { CallLog } from '@/interfaces/ILogs';
import { Card, Spin, Typography, message } from 'antd';
import moment from 'moment-timezone';
import { useLocalTimezone } from 'src/common/hooks/useLocalTimezone';
import {
	UserOutlined,
	PhoneOutlined,
	CalendarOutlined,
	GlobalOutlined,
	ClockCircleOutlined,
	CaretRightOutlined,
	PauseOutlined,
	IdcardOutlined,
	CustomerServiceOutlined,
	CopyOutlined,
	DatabaseOutlined,
	SoundOutlined,
} from '@ant-design/icons';

const { Text } = Typography;

type CallInformationCardProps = {
	callInformation: CallLog;
	audioURl: string | null;
	audioLoading: boolean;
	playbackSpeed: number;
	onPlaybackSpeedChange: (value: number) => void;
};

export const CallInformationCard: React.FC<CallInformationCardProps> = ({
	callInformation,
	audioURl,
	audioLoading,
	playbackSpeed,
	onPlaybackSpeedChange,
}) => {
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const audioRef = useRef<HTMLAudioElement>(null);
	const { timeZone } = useLocalTimezone(callInformation?.timeZone);

	const dateInNewTimezone = useMemo(() => {
		if (!callInformation?.date) {
			return 'N/A';
		}
		return moment
			.utc(callInformation.date, 'M/D/YYYY, h:mm:ss A')
			.tz(timeZone)
			.format('M/D/YYYY, h:mm:ss A');
	}, [callInformation?.date, timeZone]);

	const handleAudioLoad = () => {
		if (audioRef.current) {
			setDuration(audioRef.current.duration);
		}
	};

	const handlePlayPause = () => {
		if (audioRef.current) {
			if (isPlaying) {
				audioRef.current.pause();
			} else {
				audioRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	};

	const handleTimeUpdate = () => {
		if (audioRef.current) {
			setCurrentTime(audioRef.current.currentTime);
			setDuration(audioRef.current.duration);
		}
	};

	const formatTime = (time: number) => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	};

	const handleCopy = (value: string) => {
		navigator.clipboard
			.writeText(value)
			.then(() => {
				message.success('Copied to clipboard');
			})
			.catch(() => {
				message.error('Failed to copy');
			});
	};

	useEffect(() => {
		if (audioRef.current && typeof playbackSpeed === 'number' && isFinite(playbackSpeed)) {
			audioRef.current.playbackRate = playbackSpeed;
		}
	}, [playbackSpeed]);

	const callDetails = useMemo(
		() => [
			{
				key: 'Call Sid',
				value: callInformation?.callSid || 'N/A',
				icon: <IdcardOutlined className="text-2xl" />,
				color: 'from-blue-500 to-blue-600',
				bgColor: 'bg-blue-50',
				isMonospace: true,
				copyable: true,
			},
			{
				key: 'User Name',
				value: callInformation?.username || 'N/A',
				icon: <UserOutlined className="text-2xl" />,
				color: 'from-purple-500 to-purple-600',
				bgColor: 'bg-purple-50',
				copyable: true,
			},
			{
				key: 'To',
				value: callInformation?.to || 'N/A',
				icon: <PhoneOutlined className="text-2xl" />,
				color: 'from-green-500 to-green-600',
				bgColor: 'bg-green-50',
				copyable: true,
			},
			{
				key: 'From',
				value: callInformation?.from || 'N/A',
				icon: <CustomerServiceOutlined className="text-2xl" />,
				color: 'from-emerald-500 to-emerald-600',
				bgColor: 'bg-emerald-50',
				copyable: true,
			},
			{
				key: 'Date',
				value: dateInNewTimezone,
				icon: <CalendarOutlined className="text-2xl" />,
				color: 'from-orange-500 to-orange-600',
				bgColor: 'bg-orange-50',
				copyable: true,
			},
			{
				key: 'Time zone',
				value: timeZone,
				isTimeZone: true,
				icon: <GlobalOutlined className="text-2xl" />,
				color: 'from-indigo-500 to-indigo-600',
				bgColor: 'bg-indigo-50',
			},
			{
				key: 'Call Duration',
				value: `${callInformation?.duration || 0} seconds`,
				icon: <ClockCircleOutlined className="text-2xl" />,
				color: 'from-rose-500 to-rose-600',
				bgColor: 'bg-rose-50',
			},
			{
				key: 'Transferred Call Duration',
				value: `${callInformation?.transferredCallDuration || 0} seconds`,
				icon: <ClockCircleOutlined className="text-2xl" />,
				color: 'from-cyan-500 to-cyan-600',
				bgColor: 'bg-cyan-50',
			},
			{
				key: 'Provider',
				value: `${callInformation?.provider || ''}`,
				icon: <DatabaseOutlined className="text-2xl" />,
				color: 'from-blue-500 to-blue-600',
				bgColor: 'bg-blue-50',
			},
			{
				key: 'Voice',
				value: `${callInformation?.voice || ''}`,
				icon: <SoundOutlined className="text-2xl" />,
				color: 'from-green-500 to-green-600',
				bgColor: 'bg-green-50',
			},
		],
		[callInformation, dateInNewTimezone, timeZone],
	);

	return (
		<div className="space-y-6">
			{/* Audio Player Card */}
			{callInformation?.isRecorded && (
				<Card className="bg-white shadow-sm rounded-2xl p-4">
					<div className="space-y-4">
						{/* Header */}
						<div className="flex items-center justify-between mb-2">
							<div className="flex items-center gap-2">
								<div className="w-8 h-8 flex items-center justify-center rounded-md border bg-blue-100 animate-pulse transition-all duration-500">
									<CustomerServiceOutlined className="text-blue-600 text-lg" />
								</div>
								<Text className="text-blue-700 font-semibold text-lg">Call Recording</Text>
							</div>
						</div>

						<audio
							ref={audioRef}
							src={audioURl || ''}
							className="hidden audio"
							data-testid="audio"
							onTimeUpdate={handleTimeUpdate}
							onEnded={() => setIsPlaying(false)}
							onLoadedMetadata={handleAudioLoad}
						/>

						{/* Play Button with Elegant UI */}
						<div className="flex justify-center relative py-6">
							{audioLoading ? (
								<Spin size="large" />
							) : audioURl ? (
								<div className="relative">
									<div className="absolute inset-0 w-full h-full rounded-full border-4 border-black/60 shadow-lg animate-pulse duration-1500" />

									{/* Play Button */}
									<button
										onClick={handlePlayPause}
										className="relative w-24 h-24 rounded-full flex items-center justify-center 
                        transition-all duration-300 hover:scale-110 
                      bg-blue-600 shadow-lg border-4 border-white"
									>
										{isPlaying ? (
											<PauseOutlined className="text-5xl text-white" />
										) : (
											<CaretRightOutlined className="text-5xl text-white ml-1" />
										)}
									</button>
								</div>
							) : (
								<div className="text-gray-400">No recording available</div>
							)}
						</div>

						{/* Timeline */}
						{audioURl && (
							<div className="space-y-2">
								<div className="flex items-center gap-4">
									<div
										className="relative h-2 bg-gray-300 rounded-full cursor-pointer"
										style={{ flex: '1 1 0%', minWidth: 0 }}
										onMouseDown={(e) => {
											const rect = e.currentTarget.getBoundingClientRect();
											const percentage = (e.clientX - rect.left) / rect.width;
											if (audioRef.current) {
												audioRef.current.currentTime = percentage * duration;
											}
										}}
									>
										{/* Progress Bar */}
										<div
											className="absolute h-full rounded-full bg-blue-600 transition-all duration-150"
											style={{ width: `${(currentTime / duration) * 100}%` }}
										/>

										{/* Draggable Circle */}
										<div
											className="absolute top-1/2 w-3 h-3 bg-white rounded-full shadow-md border border-gray-400 transform -translate-y-1/2 -translate-x-1/2"
											style={{ left: `${(currentTime / duration) * 100}%` }}
										/>
									</div>

									{/* Clean Circular Speed Selector */}
									<div className="relative" style={{ flexShrink: 0, width: '48px' }}>
										<div className="w-12 h-12 rounded-full bg-white border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer flex items-center justify-center group">
											{/* Current speed display */}
											<span className="font-bold text-gray-700" style={{ fontSize: '12px' }}>
												{playbackSpeed}x
											</span>

											{/* Speed options dropdown */}
											<div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
												<div className="bg-white rounded-lg shadow-lg border border-gray-200 py-1">
													{[1, 1.5, 2, 2.5].map((speed) => (
														<button
															key={speed}
															onClick={() => onPlaybackSpeedChange(speed)}
															className={`block w-full px-3 py-1 text-left hover:bg-gray-50 transition-colors ${
																speed === playbackSpeed
																	? 'bg-blue-50 text-blue-600'
																	: 'text-gray-700'
															}`}
															style={{ fontSize: '12px' }}
														>
															{speed}x
														</button>
													))}
												</div>
											</div>
										</div>
									</div>
								</div>

								{/* Time Indicators */}
								<div
									className="flex justify-between text-gray-600 text-xs font-medium"
									style={{ paddingRight: '66px' }}
								>
									<span>{formatTime(currentTime)}</span>
									<span>{formatTime(duration)}</span>
								</div>
							</div>
						)}
					</div>
				</Card>
			)}

			{/* Call Details List */}
			<div className="space-y-3">
				{callDetails.map((detail, index) => (
					<div
						key={index}
						className="bg-white rounded-xl p-4 shadow-sm hover:shadow-md transition-all duration-300"
					>
						<div className="flex items-center gap-4">
							<div
								className={`
            w-10 h-10 rounded-lg
            ${detail.bgColor}
            flex items-center justify-center
          `}
							>
								{detail.icon}
							</div>
							<div className="flex-1">
								<Text type="secondary" className="text-sm">
									{detail.key}
								</Text>

								<div className="flex items-center gap-1">
									<Text
										strong
										className={`${detail.isMonospace ? 'font-mono text-sm' : 'text-base'}`}
									>
										{detail.value}
									</Text>
									{detail.copyable && (
										<CopyOutlined
											onClick={() => handleCopy(detail.value)}
											className="text-gray-400 hover:text-blue-500 cursor-pointer ml-1"
										/>
									)}
								</div>
							</div>
						</div>
					</div>
				))}
			</div>
		</div>
	);
};

export default CallInformationCard;
